// src/data/services.ts
// BUE University Services Catalogue - 20 Confirmed Services

export interface ServiceShortcut {
  id: string;
  title: string;
  targetUrl: string;
  icon: string;
}

export interface ServiceItem {
  id: string;
  name: string;
  description: string;
  url: string;
  category: 'administrative' | 'academic' | 'student';
  homePriority: number; // 1-9 for Home, 99 for catalogue-only
  iconKey: string;
  bgColor: string;
  iconColor: string;
  isHttps: boolean;
  keywords: string[];
  shortcuts?: ServiceShortcut[];
}

export const SERVICES: ServiceItem[] = [
  // 01. HRM (Home Priority #1)
  {
    id: 'hrm',
    name: 'HRM',
    description: 'Human Resources Management portal for university staff and faculty',
    url: 'https://hrm.bue.edu.eg/public/',
    category: 'administrative',
    homePriority: 1,
    iconKey: 'groups',
    bgColor: '#E0E7FF',
    iconColor: '#2563EB',
    isHttps: true,
    keywords: ['hr', 'human resources', 'staff', 'personnel', 'faculty', 'employees'],
  },

  // 02. IT Service Desk (Home Priority #2)
  {
    id: 'it_service_desk',
    name: 'IT Service Desk',
    description: 'Get IT support and technical assistance',
    url: 'https://servicedesk.bue.edu.eg/home',
    category: 'administrative',
    homePriority: 2,
    iconKey: 'headset',
    bgColor: '#FFE4E6',
    iconColor: '#E11D48',
    isHttps: true,
    keywords: ['it', 'tech', 'support', 'helpdesk', 'computer', 'network', 'tickets'],
  },

  // 03. UniEvent (Home Priority #3)
  {
    id: 'unievent',
    name: 'UniEvent',
    description: 'Event booking, management and support',
    url: 'https://hcms.bue.edu.eg/Event/#/login',
    category: 'administrative',
    homePriority: 3,
    iconKey: 'event',
    bgColor: '#DCFCE7',
    iconColor: '#16A34A',
    isHttps: true,
    keywords: ['event', 'booking', 'conference', 'venue', 'hall', 'support', 'calendar'],
    shortcuts: [
      {
        id: 'create_event',
        title: 'Create New Event Request',
        targetUrl: 'https://hcms.bue.edu.eg/Event/#/login',
        icon: 'add_circle',
      },
      {
        id: 'my_events',
        title: 'My Event Requests',
        targetUrl: 'https://hcms.bue.edu.eg/Event/#/login',
        icon: 'format_list_bulleted',
      },
      {
        id: 'venue_availability',
        title: 'Venue Availability',
        targetUrl: 'https://hcms.bue.edu.eg/Event/#/login',
        icon: 'calendar_month',
      },
      {
        id: 'av_technical',
        title: 'AV & Technical Requirements',
        targetUrl: 'https://hcms.bue.edu.eg/Event/#/login',
        icon: 'tv',
      },
      {
        id: 'event_guidelines',
        title: 'Events Guidelines',
        targetUrl: 'https://srs.bue.edu.eg/policies/#/login',
        icon: 'menu_book',
      },
      {
        id: 'contact_event_support',
        title: 'Contact Event Support',
        targetUrl: 'https://servicedesk.bue.edu.eg/home',
        icon: 'headset_mic',
      },
    ],
  },

  // 04. HR Service Desk (Home Priority #4)
  {
    id: 'hr_service_desk',
    name: 'HR Service Desk',
    description: 'Submit and track human resource tickets and requests',
    url: 'https://hr-servicedesk.bue.edu.eg/index.php',
    category: 'administrative',
    homePriority: 4,
    iconKey: 'support_agent',
    bgColor: '#FEE2E2',
    iconColor: '#DC2626',
    isHttps: true,
    keywords: ['hr desk', 'ticket', 'vacation', 'salary', 'payroll', 'leaves', 'inquiry'],
  },

  // 05. Estate & Facilities (Home Priority #5)
  {
    id: 'estate_facilities',
    name: 'Estate & Facilities',
    description: 'Book venues and facilities for university events',
    url: 'https://servicedesk.bue.edu.eg/app/britishuniversity/ui/ssp/pages/home',
    category: 'administrative',
    homePriority: 5,
    iconKey: 'apartment',
    bgColor: '#FEF3C7',
    iconColor: '#D97706',
    isHttps: true,
    keywords: ['estate', 'facilities', 'venue', 'maintenance', 'room', 'cleaning', 'campus'],
  },

  // 06. Student Record System (Home Priority #6)
  {
    id: 'student_record_system',
    name: 'Student Record System',
    description: 'Access your academic records, course registration and grades',
    url: 'https://srs.bue.edu.eg/bue/default.aspx',
    category: 'student',
    homePriority: 6,
    iconKey: 'assignment_ind',
    bgColor: '#DBEAFE',
    iconColor: '#2563EB',
    isHttps: true,
    keywords: ['srs', 'grades', 'transcript', 'registration', 'gpa', 'courses', 'academic'],
  },

  // 07. Postgraduate System (Home Priority #7)
  {
    id: 'postgraduate_system',
    name: 'Postgraduate System',
    description: 'Postgraduate admissions, thesis and academic tracking',
    url: 'https://pgs.bue.edu.eg/pgs/default.aspx',
    category: 'academic',
    homePriority: 7,
    iconKey: 'school',
    bgColor: '#FFE4E6',
    iconColor: '#E11D48',
    isHttps: true,
    keywords: ['pgs', 'masters', 'phd', 'postgraduate', 'thesis', 'research', 'graduate'],
  },

  // 08. UniTrav (Home Priority #8)
  {
    id: 'unitrav',
    name: 'UniTrav',
    description: 'University travel requests and conference mobility',
    url: 'https://hcms.bue.edu.eg/UniTrav/#/login',
    category: 'administrative',
    homePriority: 8,
    iconKey: 'flight',
    bgColor: '#E0F2FE',
    iconColor: '#0284C7',
    isHttps: true,
    keywords: ['travel', 'flight', 'conference', 'mission', 'visa', 'mobility', 'trips'],
  },

  // 09. UniContract (Home Priority #9)
  {
    id: 'unicontract',
    name: 'UniContract',
    description: 'University legal agreements and contracts management',
    url: 'https://unicontract.bue.edu.eg/unicontracts/#/login',
    category: 'administrative',
    homePriority: 9,
    iconKey: 'handshake',
    bgColor: '#F3E8FF',
    iconColor: '#7C3AED',
    isHttps: true,
    keywords: ['contract', 'legal', 'agreements', 'procurement', 'documents'],
  },

  // 10. University Policies
  {
    id: 'university_policies',
    name: 'University Policies',
    description: 'University regulations and governance policies',
    url: 'https://srs.bue.edu.eg/policies/#/login',
    category: 'academic',
    homePriority: 99,
    iconKey: 'policy',
    bgColor: '#CCFBF1',
    iconColor: '#0D9488',
    isHttps: true,
    keywords: ['policy', 'regulations', 'handbook', 'bylaws', 'governance', 'rules'],
  },

  // 11. MarCom Branding Kit
  {
    id: 'marcom_branding_kit',
    name: 'MarCom Branding Kit',
    description: 'Access university brand assets and guidelines',
    url: 'https://drive.google.com/drive/folders/1Egenef4iiMySzfQz7X9Y9WUsaoq1TOVl',
    category: 'administrative',
    homePriority: 99,
    iconKey: 'fingerprint',
    bgColor: '#F3E8FF',
    iconColor: '#9333EA',
    isHttps: true,
    keywords: ['marcom', 'branding', 'logo', 'marketing', 'design', 'guidelines', 'assets'],
  },

  // 12. BUE Mail
  {
    id: 'bue_mail',
    name: 'BUE Mail',
    description: 'BUE university email service',
    url: 'http://mail.bue.edu.eg/',
    category: 'student',
    homePriority: 99,
    iconKey: 'mail',
    bgColor: '#FFEDD5',
    iconColor: '#EA580C',
    isHttps: false,
    keywords: ['mail', 'email', 'webmail', 'outlook', 'inbox', 'messages'],
  },

  // 13. Scientific Library (Library Resources)
  {
    id: 'scientific_library',
    name: 'Library Resources',
    description: 'Digital library catalogue and academic research resources',
    url: 'https://lib.bue.edu.eg/',
    category: 'academic',
    homePriority: 99,
    iconKey: 'library',
    bgColor: '#E0E7FF',
    iconColor: '#4F46E5',
    isHttps: true,
    keywords: ['library', 'books', 'research', 'scientific', 'journals', 'articles', 'catalog'],
  },

  // 14. Student Portal
  {
    id: 'student_portal',
    name: 'Student Portal',
    description: 'Official student portal for academic schedules, registration, and records',
    url: 'https://srs.bue.edu.eg/studentportal/home.aspx',
    category: 'student',
    homePriority: 99,
    iconKey: 'badge',
    bgColor: '#E0E7FF',
    iconColor: '#4338CA',
    isHttps: true,
    keywords: ['student portal', 'portal', 'srs', 'grades', 'schedule', 'student', 'courses'],
  },

  // 15. Alumni
  {
    id: 'alumni',
    name: 'Alumni',
    description: 'Connect with BUE graduate network, alumni benefits, and community events',
    url: 'https://www.bue.edu.eg/alumni-section/home-page-bue',
    category: 'student',
    homePriority: 99,
    iconKey: 'workspace_premium',
    bgColor: '#FEF3C7',
    iconColor: '#D97706',
    isHttps: true,
    keywords: ['alumni', 'graduates', 'network', 'career', 'community', 'former students'],
  },

  // 16. E-Learning
  {
    id: 'elearning',
    name: 'E-Learning',
    description: 'Online learning platform, lecture materials, coursework, and virtual classrooms',
    url: 'https://www.bue.edu.eg/e-learning',
    category: 'academic',
    homePriority: 99,
    iconKey: 'computer',
    bgColor: '#DCFCE7',
    iconColor: '#15803D',
    isHttps: true,
    keywords: ['elearning', 'e-learning', 'moodle', 'online courses', 'lectures', 'assignments', 'virtual'],
  },

  // 17. Career Centre
  {
    id: 'career_centre',
    name: 'Career Centre',
    description: 'Career guidance, internships, job opportunities, and professional workshops',
    url: 'https://www.bue.edu.eg/career-centre',
    category: 'student',
    homePriority: 99,
    iconKey: 'work',
    bgColor: '#FFEDD5',
    iconColor: '#C2410C',
    isHttps: true,
    keywords: ['career', 'jobs', 'internships', 'employment', 'cv', 'work', 'recruitment'],
  },

  // 18. Academic Services
  {
    id: 'academic_services',
    name: 'Academic Services',
    description: 'Academic calendar, regulations, advising guidelines, and faculty resources',
    url: 'https://www.bue.edu.eg/academic-services',
    category: 'academic',
    homePriority: 99,
    iconKey: 'auto_stories',
    bgColor: '#DBEAFE',
    iconColor: '#1D4ED8',
    isHttps: true,
    keywords: ['academic', 'faculty', 'advising', 'curriculum', 'calendar', 'departments', 'study'],
  },

  // 19. Admissions
  {
    id: 'admissions',
    name: 'Admissions',
    description: 'Undergraduate and postgraduate admission requirements, apply online, and inquiries',
    url: 'https://www.bue.edu.eg/admissions',
    category: 'administrative',
    homePriority: 99,
    iconKey: 'how_to_reg',
    bgColor: '#CCFBF1',
    iconColor: '#0F766E',
    isHttps: true,
    keywords: ['admissions', 'apply', 'enrollment', 'intake', 'requirements', 'tuition', 'freshmen', 'join'],
  },

  // 20. Student Support & Wellbeing
  {
    id: 'student_support_wellbeing',
    name: 'Student Support & Wellbeing',
    description: 'Comprehensive student counselling, wellbeing support, and health guidance',
    url: 'https://www.bue.edu.eg/student-support-and-wellbeing',
    category: 'student',
    homePriority: 99,
    iconKey: 'health_and_safety',
    bgColor: '#FFE4E6',
    iconColor: '#BE123C',
    isHttps: true,
    keywords: ['wellbeing', 'counselling', 'mental health', 'support', 'guidance', 'wellness', 'medical', 'disability'],
  },
];

// The first 9 priority services guaranteed in exact order on Home
export const POPULAR_SERVICES: ServiceItem[] = SERVICES
  .filter((s) => s.homePriority <= 9)
  .sort((a, b) => a.homePriority - b.homePriority);

export function getServiceById(id: string): ServiceItem | undefined {
  return SERVICES.find((s) => s.id === id);
}
